import { useId } from 'react'

import { ATTRIBUTION_MODEL_LABELS, type AttributionModel } from '@/api/firstPartyTraffic'
import type { DataSource, Ga4TrafficReport } from '@/api/types'
import { Badge, FreshnessBadge } from '@/design'

const CAVEAT =
  'These figures are reported by Google Analytics 4, not counted by Competition Insights. GA4 attributes sessions and conversions by its own rules, so they will not match your order records exactly.'

/** The provenance every number on this page carries. Not optional (blueprint 06 §6.10). */
export function SourceBadge() {
  const captionId = useId()
  return (
    <span title={CAVEAT} tabIndex={0} aria-describedby={captionId} className="tr-source">
      <Badge tone="info">Source: Google Analytics 4</Badge>
      <span id={captionId} className="ci-visually-hidden">
        {CAVEAT}
      </span>
    </span>
  )
}

/** The TmT caveat: whose numbers, which visitors, which model and how far back. */
export function tmtCaveat(model: AttributionModel, lookbackDays: number | null): string {
  const lookback = lookbackDays === null ? '' : `, looking back ${lookbackDays} days before each order`
  return `These figures are counted by Competition Insights from your website's own visits and orders, from visitors who allowed analytics — never Google's. Orders are credited to a channel under the ${ATTRIBUTION_MODEL_LABELS[model]} model${lookback}.`
}

/**
 * The first-party source's provenance, the same shape as `SourceBadge` and never
 * shown beside a GA4 figure: the source, and the model its orders are credited under.
 */
/** `lookbackDays` is the report's own, so null until it has loaded. */
export function TmtSourceBadge({ model, lookbackDays }: { model: AttributionModel; lookbackDays: number | null }) {
  const captionId = useId()
  const caveat = tmtCaveat(model, lookbackDays)
  return (
    <span title={caveat} tabIndex={0} aria-describedby={captionId} className="tr-source">
      <Badge tone="accent">Source: TmT first-party · {ATTRIBUTION_MODEL_LABELS[model]}</Badge>
      <span id={captionId} className="ci-visually-hidden">
        {caveat}
      </span>
    </span>
  )
}

/** How current the sum is; a stale property is a warning, never a quiet "live". */
export function TrafficFreshness({ freshness, source }: { freshness: Ga4TrafficReport['freshness']; source: DataSource }) {
  return (
    <span className="tr-freshness">
      <FreshnessBadge meta={{ source, lastUpdatedAt: freshness.lastImportAt }} />
      {freshness.stale ? <Badge tone="warning">Stale — GA4 has not been imported for over 36 hours</Badge> : null}
    </span>
  )
}
